import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const CONTACT_EMAIL = "info@vilamouradreamscruises.com";
const RESEND_API_URL = "https://api.resend.com";
const DEFAULT_EMAIL_FROM = "Vilamoura Dreams Cruises <onboarding@resend.dev>";

const ContactSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    cruise: z.string().trim().min(1).max(100),
    phone: z
      .string()
      .trim()
      .max(30)
      .regex(/^[+()\-\s\d]{6,30}$/)
      .optional()
      .or(z.literal("")),
    email: z.string().trim().max(255).email().optional().or(z.literal("")),
    guests: z.string().trim().max(10).optional().or(z.literal("")),
    date: z.string().trim().max(20).optional().or(z.literal("")),
    comments: z.string().trim().max(1000).optional().or(z.literal("")),
    lang: z.enum(["en", "pt"]),
  })
  .refine((d) => (d.phone && d.phone.length > 0) || (d.email && d.email.length > 0), {
    message: "Phone or email required",
  });

const labels = {
  en: {
    name: "Full name",
    cruise: "Cruise",
    date: "Date",
    guests: "Guests",
    phone: "Phone",
    email: "E-mail",
    comments: "Comments",
    intro: "New booking enquiry from the website:",
    subject: "New enquiry",
  },
  pt: {
    name: "Nome completo",
    cruise: "Cruzeiro",
    date: "Data",
    guests: "Convidados",
    phone: "Telefone",
    email: "E-mail",
    comments: "Comentários",
    intro: "Novo pedido de reserva do site:",
    subject: "Novo pedido",
  },
} as const;

const SOURCE_LINE = "Source: Vilamouradreamscruises.com";
const YACHT_LINE = "Yacht: Morningstar";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export const sendContactEmail = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => ContactSchema.parse(data))
  .handler(async ({ data }) => {
    const resendApiKey = process.env.RESEND_API_KEY;
    const from = process.env.EMAIL_FROM || DEFAULT_EMAIL_FROM;

    if (!resendApiKey) {
      console.error("[contact] Missing RESEND_API_KEY");
      return {
        ok: false as const,
        error: "config" as const,
        detail: "RESEND_API_KEY is not configured.",
      };
    }

    const l = labels[data.lang];
    const lines: string[] = [
      l.intro,
      "",
      `• ${SOURCE_LINE}`,
      `• ${YACHT_LINE}`,
      "",
      `• ${l.name}: ${data.name}`,
      `• ${l.cruise}: ${data.cruise}`,
    ];
    if (data.date) lines.push(`• ${l.date}: ${data.date}`);
    if (data.guests) lines.push(`• ${l.guests}: ${data.guests}`);
    if (data.phone) lines.push(`• ${l.phone}: ${data.phone}`);
    if (data.email) lines.push(`• ${l.email}: ${data.email}`);
    if (data.comments) lines.push("", `• ${l.comments}:`, data.comments);

    const text = lines.join("\n");
    const html = `<div style="font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;color:#111">
<p>${escapeHtml(l.intro)}</p>
<ul>
  <li><strong>Source:</strong> Vilamouradreamscruises.com</li>
  <li><strong>Yacht:</strong> Morningstar</li>
</ul>
<p></p>
<ul>
  <li><strong>${escapeHtml(l.name)}:</strong> ${escapeHtml(data.name)}</li>
  <li><strong>${escapeHtml(l.cruise)}:</strong> ${escapeHtml(data.cruise)}</li>
  ${data.date ? `<li><strong>${escapeHtml(l.date)}:</strong> ${escapeHtml(data.date)}</li>` : ""}
  ${data.guests ? `<li><strong>${escapeHtml(l.guests)}:</strong> ${escapeHtml(data.guests)}</li>` : ""}
  ${data.phone ? `<li><strong>${escapeHtml(l.phone)}:</strong> ${escapeHtml(data.phone)}</li>` : ""}
  ${data.email ? `<li><strong>${escapeHtml(l.email)}:</strong> ${escapeHtml(data.email)}</li>` : ""}
</ul>
${data.comments ? `<p><strong>${escapeHtml(l.comments)}:</strong></p><p>${escapeHtml(data.comments).replace(/\n/g, "<br/>")}</p>` : ""}
</div>`;

    try {
      const response = await fetch(`${RESEND_API_URL}/emails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${resendApiKey}`,
        },
        body: JSON.stringify({
          from,
          to: [CONTACT_EMAIL],
          reply_to: data.email || undefined,
          subject: `${l.subject} — ${data.name} — ${data.cruise}`,
          text,
          html,
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        console.error("[contact] Resend API failed:", response.status, responseText);
        return {
          ok: false as const,
          error: "send" as const,
          detail: `Resend API failed [${response.status}]: ${responseText}`,
        };
      }

      console.info("[contact] Email sent via Resend", responseText);
      return { ok: true as const };
    } catch (err) {
      console.error("[contact] Resend request failed:", err);
      return {
        ok: false as const,
        error: "send" as const,
        detail: String((err as Error)?.message ?? err),
      };
    }
  });
